/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: 指标切换Segment组件
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { Segment } from 'tea-component';
import { DEFAULT_METRIC, getMetricOptions } from './constants';

import type { TMetricType } from '../../types';

import './index.scss';

import SensorTowerIcon from '@intelligence/assets/svg/dataSource/sensor-tower.svg';
import SensorTowerActivatedIcon from '@intelligence/assets/svg/dataSource/sensor-tower-activated.svg';

interface IMetricSegmentProps extends IBaseProps {
  /** 当前日期粒度 */
  granularity?: IGranularity;
  /** 当前选中的指标 */
  value?: TMetricType;
  /** 指标切换回调 */
  onChange?: ((value: TMetricType) => void) | null;
}

/**
 * MetricSegment 指标切换组件
 * @description 用于切换不同的数据指标（Downloads/DAU(MAU)/Revenue）
 */
const MetricSegment: React.FC<IMetricSegmentProps> = (props) => {
  const { style, className, granularity = 'monthly', value = DEFAULT_METRIC, onChange } = props;

  const { locale, formatMessage } = useIntlExtend();

  /**
   * 处理指标切换
   */
  const handleChange = (payload: string): void => {
    if (payload !== value) {
      onChange?.(payload as TMetricType);
    }
  };

  /**
   * 渲染带图标的选项内容
   */
  const renderOptionContent = (labelKey: string, iconSrc: string): React.ReactNode => {
    return (
      <div className="dbi-metric-segment__option">
        <img className="dbi-metric-segment__icon" alt="" src={iconSrc} />
        <span className="dbi-metric-segment__label">{formatMessage({ id: labelKey })}</span>
      </div>
    );
  };

  /**
   * 构建Segment选项
   */
  const segmentOptions = useMemo(() => {
    const metricOptions = getMetricOptions(granularity);
    return metricOptions.map((option) => {
      const optionValue = option?.value || '';
      const isActive = value === optionValue;
      const iconSrc = isActive ? SensorTowerActivatedIcon : SensorTowerIcon;

      return {
        value: optionValue,
        text: renderOptionContent(option?.labelKey || '', iconSrc),
      };
    });
  }, [locale, granularity, value]);

  return (
    <div style={style} className={classnames('dbi-metric-segment', className)}>
      <Segment options={segmentOptions} value={value} onChange={handleChange} className="segment-light" />
    </div>
  );
};

export default MetricSegment;
